'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { getFixtureEvents, getFixtureStatistics } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

export default function MatchFactsBar() {
  const searchParams = useSearchParams();
  const fixtureId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const { t } = useLanguage();

  const [facts, setFacts] = useState([
    { id: 'fact-001', labelKey: 'matchFactsGoals', value: '0' },
    { id: 'fact-002', labelKey: 'matchFactsYellowCards', value: '0' },
    { id: 'fact-003', labelKey: 'matchFactsRedCards', value: '0' },
    { id: 'fact-004', labelKey: 'matchFactsCorners', value: '0' },
    { id: 'fact-005', labelKey: 'matchFactsOffsides', value: '0' },
    { id: 'fact-006', labelKey: 'matchFactsFouls', value: '0' },
  ]);

  const fetchFacts = useCallback(async () => {
    if (!fixtureId) {
      setFacts([
        { id: 'fact-001', labelKey: 'matchFactsGoals', value: '3' },
        { id: 'fact-002', labelKey: 'matchFactsYellowCards', value: '1' },
        { id: 'fact-003', labelKey: 'matchFactsRedCards', value: '0' },
        { id: 'fact-004', labelKey: 'matchFactsCorners', value: '7' },
        { id: 'fact-005', labelKey: 'matchFactsOffsides', value: '3' },
        { id: 'fact-006', labelKey: 'matchFactsFouls', value: '18' },
      ]);
      return;
    }
    try {
      const [events, statsData] = await Promise.all([
        getFixtureEvents(fixtureId),
        getFixtureStatistics(fixtureId),
      ]);

      const goals = events.filter(e => e.type.toLowerCase() === 'goal').length;
      const yellows = events.filter(e => e.type.toLowerCase() === 'card' && e.detail.toLowerCase().includes('yellow')).length;
      const reds = events.filter(e => e.type.toLowerCase() === 'card' && e.detail.toLowerCase().includes('red')).length;

      const getStat = (name: string): number => {
        if (!statsData || statsData.length < 2) return 0;
        const homeVal = statsData[0].statistics.find(s => s.type === name)?.value;
        const awayVal = statsData[1].statistics.find(s => s.type === name)?.value;
        const parse = (v: string | number | null) => {
          if (!v) return 0;
          return parseInt(String(v).replace('%', '')) || 0;
        };
        return parse(homeVal) + parse(awayVal);
      };

      setFacts([
        { id: 'fact-001', labelKey: 'matchFactsGoals', value: String(goals) },
        { id: 'fact-002', labelKey: 'matchFactsYellowCards', value: String(yellows) },
        { id: 'fact-003', labelKey: 'matchFactsRedCards', value: String(reds) },
        { id: 'fact-004', labelKey: 'matchFactsCorners', value: String(getStat('Corner Kicks')) },
        { id: 'fact-005', labelKey: 'matchFactsOffsides', value: String(getStat('Offsides')) },
        { id: 'fact-006', labelKey: 'matchFactsFouls', value: String(getStat('Fouls')) },
      ]);
    } catch {
      // Keep existing facts
    }
  }, [fixtureId]);

  useEffect(() => {
    fetchFacts();
    const interval = setInterval(fetchFacts, 15_000);
    return () => clearInterval(interval);
  }, [fetchFacts]);

  return (
    <div
      className="rounded-xl px-6 py-4 grid grid-cols-3 md:grid-cols-6 gap-4"
      style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
    >
      {facts?.map((fact) => (
        <div key={fact?.id} className="flex flex-col items-center gap-1">
          <span className="font-mono text-2xl font-bold text-white tabular-nums">{fact?.value}</span>
          <span className="text-xs font-medium text-slate-500">{t.gameDetail[fact.labelKey as keyof typeof t.gameDetail]}</span>
        </div>
      ))}
    </div>
  );
}